import React, { useState, useEffect } from 'react';
import { Search, File, FileText, FileImage, FileSpreadsheet, Calendar, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const FileLibrarySelector = ({ onFileSelect, allowMultiple = false }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDocs, setSelectedDocs] = useState([]);

    // Pagination state
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalFiles, setTotalFiles] = useState(0);
    const ITEMS_PER_PAGE = 10;



    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            fetchFiles(1, true); // Reset and fetch new search results
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const fetchFiles = async (pageNum = 1, reset = false) => {
        try {
            if (pageNum === 1) setLoading(true);

            const token = localStorage.getItem('token');
            // Fetch unified files with pagination and search
            const params = {
                page: pageNum,
                limit: ITEMS_PER_PAGE,
                search: searchQuery
            };

            const response = await api.get('/files/company/unified', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            if (response.data.success) {
                const newFiles = response.data.files;
                const pagination = response.data.pagination;

                if (reset) {
                    setFiles(newFiles);
                } else {
                    setFiles(prev => [...prev, ...newFiles]);
                }

                if (pagination) {
                    setTotalFiles(pagination.total);
                    setHasMore(newFiles.length === ITEMS_PER_PAGE);
                    // Or strictly: pageNum < pagination.pages
                    // But checking length match is often enough for "load more"
                } else {
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Error fetching files:', error);
            toast.error('Failed to load library files');
        } finally {
            setLoading(false);
        }
    };

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchFiles(nextPage, false);
    };

    // Client-side filtering is no longer efficient with server-side pagination
    // The query is handled by server now.
    // However, if we want to ensure clean display, we rely on `files` state which is already filtered by server.
    const filteredFiles = files;

    const handleSelect = (file) => {
        if (allowMultiple) {
            const isSelected = selectedDocs.some(d => d.id === file.id);
            let newSelection;
            if (isSelected) {
                newSelection = selectedDocs.filter(d => d.id !== file.id);
            } else {
                newSelection = [...selectedDocs, file];
            }
            setSelectedDocs(newSelection);
            onFileSelect(newSelection);
        } else {
            // Single select
            setSelectedDocs([file]);
            onFileSelect([file]);
        }
    };

    const getFileIcon = (fileType) => {
        if (!fileType) return File;
        if (fileType.includes('pdf')) return FileText;
        if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('csv')) return FileSpreadsheet;
        if (fileType.includes('image')) return FileImage;
        return FileText;
    };



    return (
        <div className="h-full flex flex-col">
            {/* Search Bar */}
            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                    type="text"
                    placeholder="Search items by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-sm"
                />
            </div>

            {/* File List */}
            <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[400px] pr-2">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="animate-spin text-primary" size={24} />
                    </div>
                ) : filteredFiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <File className="w-12 h-12 text-gray-200 mb-2" />
                        <p className="text-sm">No documents found</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filteredFiles.map((file) => {
                            const Icon = getFileIcon(file.file_type);
                            const isSelected = selectedDocs.some(d => d.id === file.id);

                            return (
                                <div
                                    key={file.id}
                                    onClick={() => handleSelect(file)}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected
                                        ? 'border-primary bg-primary/5'
                                        : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'
                                        }`}>
                                        <Icon size={20} />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-gray-900'
                                            }`}>
                                            {file.file_name}
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                            <span>{Math.round(file.file_size / 1024)} KB</span>
                                            <span>•</span>
                                            <span className="flex items-center gap-1">
                                                <Calendar size={10} />
                                                {new Date(file.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>

                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-primary bg-primary' : 'border-gray-300'
                                        }`}>
                                        {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Load More Button */}
                {!loading && hasMore && (
                    <div className="py-2 text-center">
                        <button
                            onClick={handleLoadMore}
                            className="text-sm text-primary hover:text-primary-dark font-medium hover:underline"
                        >
                            Load More
                        </button>
                    </div>
                )}

                {loading && page > 1 && (
                    <div className="py-2 flex justify-center">
                        <Loader2 className="animate-spin text-primary" size={20} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileLibrarySelector;
